<template>
  <div class="nodes-page">
    <a-card class="nodes-toolbar-card" style="margin-bottom:16px">
      <div class="nodes-toolbar">
        <a-space wrap>
          <a-button type="primary" @click="addVisible = true">+ 添加服务器</a-button>
          <a-button @click="load">刷新</a-button>
          <a-button
            v-if="viewMode === 'list'"
            type="primary"
            ghost
            :disabled="selectedRowKeys.length === 0"
            @click="openBatchAction"
          >批量操作 ({{ selectedRowKeys.length }})</a-button>
        </a-space>
        <a-segmented
          v-model:value="viewMode"
          :options="[
            { label: '卡片', value: 'card' },
            { label: '列表', value: 'list' },
          ]"
        />
      </div>
    </a-card>

    <div v-if="loading" style="text-align:center;padding:48px"><a-spin /></div>
    <template v-else>
      <div v-if="viewMode === 'card'" class="node-grid">
        <a-card
          v-for="node in nodes"
          :key="node.id"
          hoverable
          class="node-grid-card"
          :body-style="{ padding: '14px' }"
          @click="showDetail(node)"
        >
          <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:10px">
            <div style="min-width:0">
              <div style="display:flex;align-items:center;gap:8px;min-width:0">
                <a-tag :color="statusColor(node.status)" style="margin:0">{{ statusLabel(node.status) }}</a-tag>
                <strong style="overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ node.name }}</strong>
                <span style="color:#9ca3af;font-size:11px;font-family:ui-monospace,Menlo,monospace">#{{ node.id }}</span>
              </div>
              <div style="margin-top:8px;color:#6b7280;font-size:12px;font-family:ui-monospace,Menlo,monospace;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">
                {{ node.resolved_ip || (node.ip_public ? 'IP 解析中或失败' : '未设置地址') }}
              </div>
            </div>
            <a-tag :color="node.active_clients > 0 ? 'blue' : 'default'" style="margin:0">
              {{ node.active_clients || 0 }} 连接
            </a-tag>
          </div>

          <div class="node-network">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
              <span style="font-weight:600;color:#374151">网络下行</span>
              <span :style="{ color: bandwidthUsage(node)?.textColor || '#374151', fontFamily: 'ui-monospace, Menlo, monospace', fontWeight: 700, fontSize: '18px' }">
                {{ formatRate(getTrafficRate(node.id).downBps) }}
                <a-tag
                  v-if="bandwidthUsage(node)"
                  :color="bandwidthUsage(node)?.tagColor"
                  style="margin-left:6px;font-size:11px;line-height:18px"
                >
                  {{ bandwidthUsage(node)?.percentLabel }}
                </a-tag>
              </span>
            </div>
            <div
              v-if="bandwidthUsage(node)"
              style="height:6px;background:#e5e7eb;border-radius:999px;margin-top:7px;overflow:hidden"
            >
              <div
                :style="{
                  height: '100%',
                  width: bandwidthUsage(node)?.barWidth,
                  background: bandwidthUsage(node)?.barColor,
                }"
              />
            </div>
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:8px">
              <span style="font-weight:600;color:#374151">上行</span>
              <span style="font-family:ui-monospace,Menlo,monospace;font-weight:700;color:#374151">
                {{ formatRate(getTrafficRate(node.id).upBps) }}
              </span>
            </div>
            <div v-if="node.net_stats" class="node-net-stats">
              <div class="node-net-stats-title">近 {{ node.net_stats.window_hours }} 小时下行</div>
              <div class="node-net-stats-grid">
                <div><span>最小</span><strong>{{ formatRate(node.net_stats.min_bps) }}</strong></div>
                <div><span>平均</span><strong>{{ formatRate(node.net_stats.avg_bps) }}</strong></div>
                <div><span>最大</span><strong>{{ formatRate(node.net_stats.max_bps) }}<em v-if="limitPercent(node, node.net_stats.max_bps) != null">占限速 {{ limitPercent(node, node.net_stats.max_bps) }}%</em></strong></div>
              </div>
              <div class="node-net-stats-peak">
                最大值持续 {{ formatPeakDuration(node.net_stats.max_duration_seconds) }}
                · {{ formatDateTime(node.net_stats.max_at) }}
              </div>
            </div>
          </div>

          <div style="display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px;margin-top:14px">
            <div class="node-mini-stat">
              <span>端口</span>
              <strong>{{ node.used_ports }}/{{ node.capacity_ports }}</strong>
              <PortDots :used="node.used_ports" :capacity="node.capacity_ports" />
            </div>
            <div class="node-mini-stat">
              <span>CPU</span>
              <strong>{{ node.cpu_percent ? node.cpu_percent + '%' : '-' }}</strong>
            </div>
            <div class="node-mini-stat">
              <span>内存</span>
              <strong>{{ node.mem_percent ? node.mem_percent + '%' : '-' }}</strong>
            </div>
          </div>

          <div class="node-stability">
            <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
              <a-tooltip title="仅统计目标端口 13000–13020；登录端口 13100–13104 不计入；连续 48 小时无游戏掉线即判定为稳定">
                <span>游戏连接稳定性 ⓘ</span>
              </a-tooltip>
              <a-tag :color="nodeStability(node).color" style="margin:0">
                {{ nodeStability(node).label }}
              </a-tag>
            </div>
            <div class="node-stability-detail">
              24小时掉线 {{ node.game_disconnect_count_24h || 0 }} 次 ·
              7天 {{ node.game_disconnect_count_7d || 0 }} 次 ·
              {{ formatGameDisconnectFrequency(node.game_disconnect_count_7d) }}
            </div>
            <div class="node-stability-duration">
              {{ formatGameDisconnectFreeDuration(node.game_disconnect_free_seconds) }}
            </div>
            <div v-if="node.last_game_disconnect_at" class="node-stability-last">
              最近游戏掉线 {{ formatDateTime(node.last_game_disconnect_at) }}
            </div>
          </div>

          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-top:14px;color:#9ca3af;font-size:11px">
            <span>累计 ↓{{ formatBytes(node.bytes_down) }}</span>
            <span>{{ expireShort(node.expire_at) }}</span>
          </div>
        </a-card>
      </div>

      <template v-else>
        <a-table
          :columns="nodeColumns"
          :data-source="nodes"
          row-key="id"
          size="small"
          :row-selection="rowSelection"
          :pagination="false"
          :loading="loading"
          :scroll="{ x: 1880 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'name'">
              <a @click="showDetail(record)" style="font-weight:600">{{ record.name }}</a>
              <span style="margin-left:6px;color:#9ca3af;font-size:11px;font-family:ui-monospace,Menlo,monospace">#{{ record.id }}</span>
            </template>
            <template v-else-if="column.key === 'status'">
              <a-tag :color="statusColor(record.status)">{{ statusLabel(record.status) }}</a-tag>
            </template>
            <template v-else-if="column.key === 'stability'">
              <a-tag :color="nodeStability(record).color" style="margin:0">
                {{ nodeStability(record).label }}
              </a-tag>
              <div style="font-size:11px;color:#6b7280;line-height:1.7;margin-top:4px">
                <div>游戏掉线 24h {{ record.game_disconnect_count_24h || 0 }} 次 · 7天 {{ record.game_disconnect_count_7d || 0 }} 次</div>
                <div>{{ formatGameDisconnectFrequency(record.game_disconnect_count_7d) }}</div>
                <div class="node-stability-duration">{{ formatGameDisconnectFreeDuration(record.game_disconnect_free_seconds) }}</div>
                <div v-if="record.last_game_disconnect_at">最近 {{ formatDateTime(record.last_game_disconnect_at) }}</div>
              </div>
            </template>
            <template v-else-if="column.key === 'ip'">
              <span style="font-family:ui-monospace,Menlo,monospace">{{ record.resolved_ip || (record.ip_public ? 'IP 解析中或失败' : '未设置地址') }}</span>
            </template>
            <template v-else-if="column.key === 'version'">
              <template v-if="record.agent_version">
                <a-tooltip :title="record.agent_version">
                  <a-tag color="purple" style="max-width:230px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-family:ui-monospace,Menlo,monospace">
                    {{ record.agent_version }}
                  </a-tag>
                </a-tooltip>
                <div style="font-size:11px;color:#6b7280;font-family:ui-monospace,Menlo,monospace;margin-top:3px">
                  SHA {{ record.agent_git_sha || '—' }}
                </div>
              </template>
              <span v-else style="color:#9ca3af">未上报</span>
              <div style="font-size:11px;color:#6b7280;margin-top:3px">
                升级 {{ formatDateTime(record.upgraded_at, '从未升级') }}
              </div>
            </template>
            <template v-else-if="column.key === 'resources'">
              <div style="font-family:ui-monospace,Menlo,monospace;font-size:12px;line-height:1.7">
                <div>CPU {{ percentLabel(record.cpu_percent) }}</div>
                <div>MEM {{ percentLabel(record.mem_percent) }}</div>
                <div>DSK {{ percentLabel(record.disk_percent) }}</div>
              </div>
            </template>
            <template v-else-if="column.key === 'ports'">
              <div style="font-family:ui-monospace,Menlo,monospace">{{ record.used_ports }}/{{ record.capacity_ports }}</div>
              <div style="font-size:11px;color:#6b7280;margin-top:3px">连接 {{ record.active_clients || 0 }}</div>
            </template>
            <template v-else-if="column.key === 'rate'">
              <div style="font-family:ui-monospace,Menlo,monospace;line-height:1.7">
                <div :style="{ color: bandwidthUsage(record)?.textColor || '#374151' }">
                  ↓ {{ formatRate(getTrafficRate(record.id).downBps) }}
                  <a-tag
                    v-if="bandwidthUsage(record)"
                    :color="bandwidthUsage(record)?.tagColor"
                    style="margin-left:5px;font-size:11px;line-height:18px"
                  >{{ bandwidthUsage(record)?.percentLabel }}</a-tag>
                </div>
                <div>↑ {{ formatRate(getTrafficRate(record.id).upBps) }}</div>
              </div>
              <div style="font-size:11px;color:#9ca3af;margin-top:3px;line-height:1.6">
                累计 ↑{{ formatBytes(record.bytes_up) }} · ↓{{ formatBytes(record.bytes_down) }}
              </div>
            </template>
            <template v-else-if="column.key === 'cost'">
              <span v-if="record.total_cost_cents != null">¥{{ formatCost(record.total_cost_cents) }}</span>
              <span v-else style="color:#f5222d">未设置</span>
            </template>
            <template v-else-if="column.key === 'expire'">
              <div>{{ expireShort(record.expire_at) }}</div>
              <div v-if="record.expire_at" style="font-size:11px;color:#6b7280;margin-top:3px">
                {{ formatDateTime(record.expire_at) }}
              </div>
            </template>
            <template v-else-if="column.key === 'actions'">
              <a-space>
                <a-button type="link" size="small" @click="showDetail(record)">详情</a-button>
              </a-space>
            </template>
          </template>
        </a-table>
      </template>
      <a-empty v-if="nodes.length === 0" description="暂无服务器" />
    </template>

    <AddNodeModal v-model:visible="addVisible" @added="load" />
    <NodeDetailDrawer
      v-model:visible="detailVisible"
      :node="selectedNode"
      :traffic-rate="selectedNode ? nodeTrafficRates[selectedNode.id] : undefined"
      @refresh="load"
    />
    <BatchNodeActionModal
      v-model:visible="batchActionOpen"
      :node-ids="selectedRowKeys"
      :nodes="nodes"
      @success="onBatchActionSuccess"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { listNodes } from '../api/nodes';
import NodeDetailDrawer from '../components/NodeDetailDrawer.vue';
import PortDots from '../components/PortDots.vue';
import AddNodeModal from '../components/AddNodeModal.vue';
import BatchNodeActionModal from '../components/BatchNodeActionModal.vue';
import {
  describeNodeStability,
  formatGameDisconnectFreeDuration,
  formatGameDisconnectFrequency,
} from '../components/nodeStability';
import type { NodeItem } from '../types';
import { formatTrafficBytes, formatTrafficRate } from '../utils/trafficFormat';

const route = useRoute();
const router = useRouter();
const nodes = ref<NodeItem[]>([]);
const loading = ref(true);
const addVisible = ref(false);
const detailVisible = ref(false);
const selectedNode = ref<NodeItem | null>(null);
const viewMode = ref<'card' | 'list'>('card');
interface NodeTrafficRate {
  upBps: number;
  downBps: number;
}
const nodeTrafficRates = ref<Record<number, NodeTrafficRate>>({});

// Batch node action (list view only). We bind to a-table's
// row-selection and drive the toolbar "批量操作 (N)" button from
// the selected count.
const selectedRowKeys = ref<number[]>([]);
const rowSelection = computed(() => ({
  selectedRowKeys: selectedRowKeys.value,
  onChange: (keys: (string | number)[]) => {
    selectedRowKeys.value = keys.map((k) => Number(k));
  },
  preserveSelectedRowKeys: true,
}));
const batchActionOpen = ref(false);
const openBatchAction = () => {
  if (selectedRowKeys.value.length === 0) {
    message.warning('请先勾选服务器');
    return;
  }
  batchActionOpen.value = true;
};
const onBatchActionSuccess = (payload: { dispatched: number; skipped: number; ws_failed: number }) => {
  const parts: string[] = [`已派发 ${payload.dispatched}`];
  if (payload.skipped) parts.push(`跳过 ${payload.skipped}`);
  if (payload.ws_failed) parts.push(`WS 失败 ${payload.ws_failed}`);
  message.success(parts.join('，'));
  load();
  selectedRowKeys.value = [];
  batchActionOpen.value = false;
};

// Columns for the list-view a-table. Card view doesn't use this.
const nodeColumns = [
  { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
  { title: '名称', key: 'name', width: 200 },
  { title: '状态', key: 'status', dataIndex: 'status', width: 90 },
  { title: '游戏连接稳定性', key: 'stability', width: 280 },
  { title: '公网 IP', key: 'ip', width: 180 },
  { title: 'Agent / 升级时间', key: 'version', width: 250 },
  { title: '资源', key: 'resources', width: 150 },
  { title: '端口 / 连接', key: 'ports', width: 110 },
  { title: '实时 / 累计流量', key: 'rate', width: 260 },
  { title: '总成本', key: 'cost', width: 110 },
  { title: '到期', key: 'expire', width: 170 },
  { title: '操作', key: 'actions', width: 80, fixed: 'right' as const },
];

const getTrafficRate = (nodeId: number): NodeTrafficRate =>
  nodeTrafficRates.value[nodeId] || { upBps: 0, downBps: 0 };

const formatRate = formatTrafficRate;

const formatBytes = formatTrafficBytes;

const formatPeakDuration = (seconds: number): string => {
  if (!seconds || seconds < 300) return '不足 5 分钟';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} 分钟`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} 小时 ${m} 分钟` : `${h} 小时`;
};

const formatDateTime = (value: string | null | undefined, fallback = '—'): string => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString('zh-CN', { hour12: false });
};

const percentLabel = (value: string | number | null | undefined): string => {
  if (value == null || value === '') return '—';
  return `${value}%`;
};

const nodeStability = (node: NodeItem) => describeNodeStability(node);

const formatCost = (costCents: number): string => (Number(costCents) / 100).toFixed(2);

const statusColor = (status: string) => {
  switch (status) {
    case 'online': return 'green';
    case 'offline': return 'default';
    case 'new': return 'blue';
    default: return 'default';
  }
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'online': return '在线';
    case 'offline': return '离线';
    case 'new': return '未加入';
    default: return status;
  }
};

// Share of the node's download limit that a rate represents; null when no limit is set.
const limitPercent = (node: NodeItem, bps: number): string | null => {
  const mbps = Number(node.bandwidth_down_mbps || 0);
  if (!Number.isFinite(mbps) || mbps <= 0) return null;
  return ((bps / ((mbps * 1024 * 1024) / 8)) * 100).toFixed(0);
};

const bandwidthUsage = (node: NodeItem) => {
  const mbps = Number(node.bandwidth_down_mbps || 0);
  if (!Number.isFinite(mbps) || mbps <= 0) return null;
  const limitBps = (mbps * 1024 * 1024) / 8;
  const percent = Math.max(0, (getTrafficRate(node.id).downBps / limitBps) * 100);
  const base = {
    percent,
    percentLabel: `${percent.toFixed(0)}%`,
    barWidth: `${Math.min(100, percent).toFixed(0)}%`,
  };
  if (percent >= 90) {
    return { ...base, tagColor: 'red', barColor: '#ef4444', textColor: '#dc2626' };
  }
  if (percent >= 70) {
    return { ...base, tagColor: 'gold', barColor: '#f59e0b', textColor: '#ca8a04' };
  }
  return { ...base, tagColor: 'green', barColor: '#22c55e', textColor: '#16a34a' };
};

const expireShort = (s: string | null) => {
  if (!s) return '永不过期';
  const t = new Date(s).getTime();
  if (Number.isNaN(t)) return '-';
  return t < Date.now() ? '已过期' : `到期 ${new Date(s).toLocaleDateString('zh-CN')}`;
};

// Refresh every 5s so node status / CPU / MEM stay current.
let refreshTimer: number | undefined;
const startAutoRefresh = () => {
  if (refreshTimer) return;
  refreshTimer = window.setInterval(load, 5000);
};
const stopAutoRefresh = () => {
  if (refreshTimer) {
    window.clearInterval(refreshTimer);
    refreshTimer = undefined;
  }
};

const load = async () => {
  // Don't toggle the spinner on every periodic refresh — only on the
  // first user-triggered one. Keeps the UI stable.
  if (!refreshTimer) loading.value = true;
  try {
    const r = await listNodes();
    const nextNodes: NodeItem[] = r.data || [];
    const nextRates: Record<number, NodeTrafficRate> = {};
    for (const n of nextNodes) {
      nextRates[n.id] = { upBps: Number(n.up_bps || 0), downBps: Number(n.down_bps || 0) };
    }
    nodes.value = nextNodes;
    nodeTrafficRates.value = nextRates;
  } finally {
    loading.value = false;
  }
};

const showDetail = (n: NodeItem) => {
  selectedNode.value = n;
  detailVisible.value = true;
};

watch(
  () => route.query.create,
  async (create) => {
    if (create !== '1') return;
    addVisible.value = true;
    const query = { ...route.query };
    delete query.create;
    await router.replace({ path: route.path, query });
  },
  { immediate: true },
);

onMounted(() => {
  load().then(() => startAutoRefresh());
});
onUnmounted(stopAutoRefresh);
</script>

<style scoped>
.nodes-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.node-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 12px;
}

.node-grid-card {
  border-radius: 8px;
}

.node-network {
  margin-top: 14px;
  padding: 10px 12px;
  border: 1px solid #dbeafe;
  background: #f5f9ff;
  border-radius: 8px;
  font-size: 12px;
  color: #6b7280;
}
.node-net-stats { margin-top: 10px; padding-top: 8px; border-top: 1px dashed #bfdbfe; }
.node-net-stats-title { font-weight: 600; color: #374151; margin-bottom: 4px; }
.node-net-stats-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
.node-net-stats-grid span { display: block; color: #9ca3af; font-size: 11px; }
.node-net-stats-grid strong { font-family: ui-monospace, Menlo, monospace; color: #374151; font-size: 12px; }
.node-net-stats-grid em { display: block; font-style: normal; color: #6b7280; font-size: 11px; }
.node-net-stats-peak { margin-top: 4px; color: #6b7280; font-size: 11px; }
.node-mini-stat {
  min-width: 0;
  border: 1px solid #eef2f7;
  border-radius: 6px;
  padding: 8px;
  background: #f8fafc;
}

.node-mini-stat span {
  display: block;
  color: #6b7280;
  font-size: 11px;
  line-height: 1.2;
}

.node-mini-stat strong {
  display: block;
  margin-top: 4px;
  color: #111827;
  font-family: ui-monospace, Menlo, monospace;
  font-size: 13px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-stability {
  margin-top: 12px;
  padding: 9px 10px;
  border: 1px solid #eef2f7;
  border-radius: 6px;
  background: #f8fafc;
  color: #4b5563;
  font-size: 12px;
}

.node-stability-detail {
  margin-top: 6px;
  color: #6b7280;
  line-height: 1.6;
}

.node-stability-last {
  margin-top: 2px;
  color: #9ca3af;
  font-size: 11px;
}

.node-stability-duration {
  margin-top: 2px;
  color: #1677ff;
  font-weight: 600;
}

@media (max-width: 767px) {
  .nodes-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .nodes-toolbar :deep(.ant-space) {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 0.65fr);
    width: 100%;
  }

  .nodes-toolbar :deep(.ant-btn),
  .nodes-toolbar :deep(.ant-segmented) {
    width: 100%;
  }

  .node-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .node-grid-card {
    overflow: hidden;
  }
}
</style>
